'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DataTable.Header
 *
 * Renders <thead> with one <tr> per header group.
 * Sortable columns render as <th scope="col" aria-sort> with a keyboard-activated
 * sort button inside. Non-sortable columns render as plain <th scope="col">.
 *
 * The sticky prop adds position:sticky; top:0 and writes the measured height
 * into the CSS custom property --dt-sticky-header-height on the row, enabling
 * child scroll-margin-block-start.
 */
import { useRef, useEffect, useState } from 'react';
import { flexRender } from '@tanstack/react-table';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { useDataTable } from './context';
import styles from './DataTable.module.css';
function sortIcon(dir) {
    if (dir === 'ascending')
        return _jsx(ArrowUp, { size: 12, "aria-hidden": "true" });
    if (dir === 'descending')
        return _jsx(ArrowDown, { size: 12, "aria-hidden": "true" });
    return _jsx(ChevronsUpDown, { size: 12, "aria-hidden": "true" });
}
function ariaSortAttr(dir) {
    if (dir === 'ascending')
        return 'ascending';
    if (dir === 'descending')
        return 'descending';
    return 'none';
}
export function Header({ sticky = false }) {
    const { table, selectionEnabled, sortable, stickyHeaderVar } = useDataTable();
    const rowRef = useRef(null);
    const announceRef = useRef(null);
    const announceIdRef = useRef(null);
    const [announcement, setAnnouncement] = useState('');
    // Write sticky header height into CSS var for scroll-margin
    useEffect(() => {
        if (!sticky || !rowRef.current)
            return;
        const row = rowRef.current;
        const update = () => {
            const h = row.getBoundingClientRect().height;
            row.closest('table')?.parentElement?.style.setProperty(stickyHeaderVar, `${h}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(row);
        return () => ro.disconnect();
    }, [sticky, stickyHeaderVar]);
    function announce(msg) {
        if (announceIdRef.current)
            clearTimeout(announceIdRef.current);
        announceIdRef.current = setTimeout(() => setAnnouncement(msg), 50);
    }
    function handleSortToggle(e, column) {
        if ('key' in e && e.key !== 'Enter' && e.key !== ' ')
            return;
        e.preventDefault();
        column.toggleSorting();
        const dir = column.getIsSorted();
        const newDir = dir === 'asc' ? 'descending' : dir === 'desc' ? 'none' : 'ascending';
        announce(`Sorted by ${String(column.columnDef.header ?? column.id)}, ${newDir}`);
    }
    // Tristate select-all checkbox state
    function selectAllState() {
        if (table.getIsAllPageRowsSelected())
            return true;
        if (table.getIsSomePageRowsSelected())
            return 'indeterminate';
        return false;
    }
    return (_jsxs("thead", { className: sticky ? styles.theadSticky : styles.thead, role: "rowgroup", children: [_jsx("tr", { "aria-hidden": "true", style: { display: 'none' }, children: _jsx("td", { children: _jsx("span", { ref: announceRef, role: "status", "aria-live": "polite", "aria-atomic": "true", className: styles.srOnly, children: announcement }) }) }), table.getHeaderGroups().map((hg) => (_jsxs("tr", { ref: rowRef, role: "row", className: styles.headerRow, children: [selectionEnabled && (_jsx("th", { scope: "col", role: "columnheader", className: styles.thCheckbox, children: (() => {
                            const state = selectAllState();
                            return (_jsx("input", { type: "checkbox", className: styles.checkbox, checked: state === true, ref: (el) => {
                                    if (el)
                                        el.indeterminate = state === 'indeterminate';
                                }, onChange: () => table.toggleAllPageRowsSelected(), "aria-label": "Select all rows" }));
                        })() })), hg.headers.map((header) => {
                        const col = header.column;
                        const isColSortable = sortable && col.getCanSort();
                        const sortDir = col.getIsSorted() === 'asc'
                            ? 'ascending'
                            : col.getIsSorted() === 'desc'
                                ? 'descending'
                                : false;
                        const meta = col.columnDef.meta;
                        const width = typeof meta?.width === 'number' ? `${meta.width}px` : meta?.width;
                        const align = meta?.align;
                        return (_jsx("th", { scope: "col", role: "columnheader", colSpan: header.colSpan, className: [styles.th, isColSortable ? styles.thSortable : ''].filter(Boolean).join(' '), "aria-sort": isColSortable ? ariaSortAttr(sortDir) : undefined, "data-mobile-hide": meta?.mobileHide ? '' : undefined, style: {
                                scrollMarginBlockStart: sticky
                                    ? `var(${stickyHeaderVar}, 0)`
                                    : undefined,
                                '--dt-col-width': width,
                                '--dt-col-align': align,
                            }, children: header.isPlaceholder ? null : isColSortable ? (_jsxs("button", { type: "button", className: styles.sortButton, onClick: (e) => handleSortToggle(e, col), onKeyDown: (e) => handleSortToggle(e, col), "aria-label": `Sort by ${String(header.column.columnDef.header ?? header.column.id)}`, children: [flexRender(col.columnDef.header, header.getContext()), _jsx("span", { className: styles.sortIcon, children: sortIcon(sortDir) })] })) : (flexRender(col.columnDef.header, header.getContext())) }, header.id));
                    })] }, hg.id)))] }));
}
//# sourceMappingURL=Header.js.map